import { SbMediaDevices } from './media-devices';

export enum SbMediaStreamType {
  camera = 'camera',
  display = 'display',
}

export interface CameraStreamArgs {
  audio?: string | boolean;
  video?: string | boolean;
}

type OnStreamChangeCallback = (ev: {
  streamType: SbMediaStreamType;
  stream: MediaStream | null;
}) => void;

export class SbMediaStreams {
  #mediaDevices: SbMediaDevices;
  #streams = {
    [SbMediaStreamType.camera]: null,
    [SbMediaStreamType.display]: null,
  } as Record<SbMediaStreamType, null | MediaStream>;
  #streamChangeCallbacks: OnStreamChangeCallback[] = [];
  #stopVideoTracks: null | (() => void) = null;
  #stopAudioTracks: null | (() => void) = null;

  constructor(mediaDevices: SbMediaDevices) {
    this.#mediaDevices = mediaDevices;
  }

  /**
   * Add an event listener for when a media stream changes. Does not trigger on
   * individual track changes, only when the stream starts or stops. Returns an
   * unsubscribe function.
   */
  onStreamChange(cb: OnStreamChangeCallback): () => void {
    this.#streamChangeCallbacks.push(cb);
    return () => {
      this.#streamChangeCallbacks = this.#streamChangeCallbacks.filter(_cb => _cb === cb);
    };
  }

  /**
   * Starts the camera stream with audio and / or video. Returns the an object
   * containing the stream, audioDeviceId (if available) and videoDeviceId (if
   * available.) Also triggers registered stream change callbacks.
   */
  async startCameraStream({ audio, video }: CameraStreamArgs) {
    const res = await this.#mediaDevices.setupCameraMediaStream({ audio, video });
    this.#stopVideoTracks = res.stopVideo;
    this.#stopAudioTracks = res.stopAudio;
    this.#addStream(SbMediaStreamType.camera, res.stream);
    return res;
  }

  /**
   * Starts the display stream. Returns the stream, and triggers registered
   * stream change callbacks.
   */
  async startDisplayStream() {
    const stream = await this.#mediaDevices.setupDisplayMediaStream();
    this.#addStream(SbMediaStreamType.display, stream);
    return stream;
  }

  stopCameraStream() {
    if (this.#stopVideoTracks) {
      this.#stopVideoTracks();
    }
    return this.#stopStream(SbMediaStreamType.camera);
  }

  stopDisplayStream() {
    return this.#stopStream(SbMediaStreamType.display);
  }

  stopCameraAudioTrack() {
    if (this.#stopAudioTracks) {
      this.#stopAudioTracks();
    }
  }

  stopCameraVideoTrack() {
    if (this.#stopVideoTracks) {
      this.#stopVideoTracks();
    }
  }

  getCameraStream() {
    return this.#streams[SbMediaStreamType.camera];
  }

  getDisplayStream() {
    return this.#streams[SbMediaStreamType.display];
  }

  getCameraVideoTrack() {
    return this.#getStreamTrack(SbMediaStreamType.camera, 'video');
  }

  getCameraAudioTrack() {
    return this.#getStreamTrack(SbMediaStreamType.camera, 'audio');
  }

  getDisplayVideoTrack() {
    return this.#getStreamTrack(SbMediaStreamType.display, 'video');
  }

  getAllTracks(): MediaStreamTrack[] {
    return [
      this.getCameraAudioTrack(),
      this.getCameraVideoTrack(),
      this.getDisplayVideoTrack(),
    ].filter(e => Boolean(e)) as MediaStreamTrack[];
  }

  #getStreamTrack(streamType: SbMediaStreamType, trackKind: 'audio' | 'video') {
    const tracks = this.#streams[streamType]
      ?.getTracks()
      .filter(t => t.kind === trackKind);
    if (tracks?.length) {
      return tracks[0];
    }
    return null;
  }

  #addStream(streamType: SbMediaStreamType, stream: MediaStream) {
    this.#stopStream(streamType, true);
    this.#streams[streamType] = stream;
    this.#bindStreamEvents(streamType, stream);
    this.#emitStreamChange(streamType);
  }

  #stopStream(streamType: SbMediaStreamType, noEmit = false) {
    const stream = this.#streams[streamType];
    if (!stream) return false;
    stream.getTracks().forEach(t => t.stop());
    this.#streams[streamType] = null;
    if (!noEmit) {
      this.#emitStreamChange(streamType);
    }
    return true;
  }

  #emitStreamChange(streamType: SbMediaStreamType) {
    const stream = this.#streams[streamType];
    this.#streamChangeCallbacks.forEach(cb => cb({ streamType, stream }));
  }

  #bindStreamEvents(streamType: SbMediaStreamType, stream: MediaStream) {
    // If a stream stops all of its tracks, consider it dead. Some user agents
    // provide users controls to end streams independent of our user interfaces.
    const bindTrackEvent = (track: MediaStreamTrack) => {
      track.addEventListener('ended', () => {
        const numTracks = stream.getTracks().length;
        if (!numTracks) {
          this.#stopStream(streamType);
        }
      });
    };
    stream.getTracks().forEach(bindTrackEvent);
    stream.addEventListener('addtrack', ev => bindTrackEvent(ev.track));
  }
}
